import { withListShortcuts } from './shortcuts';
import type { CreateEnhanceEditorState } from '../../types';
import type { ListPluginPropertyOptions } from './types';
import { isNodeTypeIn } from '../../utils/isNodeTypeIn';
import { Editor, Element, Transforms, Node, Range, Point } from '../../core';
import { BRACKET_TYPES } from '../../constants';
import { isEmptyParagraph } from '../paragraph/utils';
import { downgradeOrphanListItemVariantAtNode, isListChildValid, removeEmptyLists } from './utils';
import { PARAGRAPH_PLUGIN_ID } from '../paragraph/types';
import { LIST_PLUGIN_ID } from './types';
import { LIST_VARIANTS } from './constants';
import {
  getSelectedBracketEntry,
  isSelectionInEmptyBracket,
  shouldReplaceBracketContentOnPaste,
} from '../../utils/bracketPaste';
import {
  convertListNodesToPlainTextParagraphs,
  countListItemsInFragment,
  flattenTopLevelListNodesToParagraphNodes,
} from '../../utils/listPaste';
import type { NodeEntry } from 'slate';

const isCursorInEmptyParagraph = (editor: Editor): boolean => {
  if (editor.selection == null || !Range.isCollapsed(editor.selection)) {
    return false;
  }

  const currentBlock = Editor.above(editor, {
    at: editor.selection,
    match: (node) => Element.isElement(node) && Editor.isBlock(editor, node),
    mode: 'lowest',
  });

  if (currentBlock == null) {
    return false;
  }

  const [node] = currentBlock;
  return (
    Element.isElement(node) &&
    node.type === PARAGRAPH_PLUGIN_ID &&
    node.variant == null &&
    Node.string(node) === ''
  );
};

const isEntireParagraphLineSelected = (editor: Editor): boolean => {
  if (editor.selection == null || Range.isCollapsed(editor.selection)) {
    return false;
  }

  const [selectionStart, selectionEnd] = Range.edges(editor.selection);
  const startBlockEntry = Editor.above(editor, {
    at: selectionStart,
    match: (node) => Element.isElement(node) && Editor.isBlock(editor, node),
    mode: 'lowest',
  });
  const endBlockEntry = Editor.above(editor, {
    at: selectionEnd,
    match: (node) => Element.isElement(node) && Editor.isBlock(editor, node),
    mode: 'lowest',
  });

  if (startBlockEntry == null || endBlockEntry == null) {
    return false;
  }

  const [startBlockNode, startBlockPath] = startBlockEntry;
  const [endBlockNode, endBlockPath] = endBlockEntry;
  if (
    !Element.isElement(startBlockNode) ||
    !Element.isElement(endBlockNode) ||
    startBlockNode.type !== PARAGRAPH_PLUGIN_ID ||
    endBlockNode.type !== PARAGRAPH_PLUGIN_ID ||
    startBlockNode.variant != null ||
    endBlockNode.variant != null
  ) {
    return false;
  }

  if (!Point.equals(selectionStart, Editor.start(editor, startBlockPath))) {
    return false;
  }

  if (!Point.equals(selectionEnd, Editor.end(editor, endBlockPath))) {
    return false;
  }

  return true;
};

export const enhanceEditorStateList: CreateEnhanceEditorState<ListPluginPropertyOptions> =
  (options) => (editor: Editor) => {
    const { insertFragment, normalizeNode } = editor;

    const hasListNodes = (fragment: Array<Node>): boolean => {
      return fragment.some((node) => Element.isElement(node) && node.type === LIST_PLUGIN_ID);
    };

    const insertChildrenFromListNode = (listChildren: Array<Node>) => {
      listChildren.forEach((listChild: Node, childIndex: number) => {
        insertNodesIntoList([listChild]);
        if (childIndex !== listChildren.length - 1) {
          // Insert a line break after each list item except for the last one
          Editor.insertBreak(editor);
        }
      });
    };

    const insertChildrenFromContainerNode = (nodeChildren: Array<Node>, nodeIndex: number) => {
      // Insert each child of the node
      nodeChildren.forEach((child, childIndex) => {
        if (child.text != null && child.text !== '' && typeof child.text === 'string') {
          if (nodeIndex !== 0 || childIndex !== 0) {
            Editor.insertBreak(editor);
          }
          Transforms.insertText(editor, child.text);
        } else if (Array.isArray(child.children) && child.children) {
          insertNodesIntoList([child]);
        }
      });
    };

    const insertSingleNodeIntoList = (node: Node, index: number) => {
      if (typeof node.text === 'string' && node.text !== '') {
        Transforms.insertText(editor, node.text);
        return;
      }

      if (Array.isArray(node.children) && node.type === LIST_PLUGIN_ID) {
        insertChildrenFromListNode(node.children);
        return;
      }

      if (BRACKET_TYPES.includes(node.type)) {
        if (Array.isArray(node.children)) {
          const bracketSelectedEntry = getSelectedBracketEntry(editor);
          if (bracketSelectedEntry != null) {
            const shouldReplaceBracketContents = shouldReplaceBracketContentOnPaste(
              editor,
              bracketSelectedEntry[1]
            );

            if (shouldReplaceBracketContents) {
              Transforms.select(editor, {
                anchor: Editor.start(editor, bracketSelectedEntry[1]),
                focus: Editor.end(editor, bracketSelectedEntry[1]),
              });
              Transforms.delete(editor);
            }

            insertNodesIntoList(node.children);
            return;
          }

          if (isSelectionInEmptyBracket(editor)) {
            insertNodesIntoList(node.children);
            return;
          }
        }

        Transforms.insertNodes(editor, node);
        return;
      }

      if (isEmptyParagraph(node)) {
        Editor.insertBreak(editor);
        return;
      }

      if (Array.isArray(node.children) && node.children != null) {
        insertChildrenFromContainerNode(node.children, index);
      }
    };

    const insertNodesIntoList = (nodes: Array<Node>) => {
      if (nodes == null) {
        return;
      }

      nodes.forEach((node, index) => {
        insertSingleNodeIntoList(node, index);
      });
    };

    editor.insertFragment = (fragment: Array<Node>) => {
      if (isNodeTypeIn(editor, options.pluginID)) {
        return insertNodesIntoList(fragment);
      }

      if (hasListNodes(fragment) && isSelectionInEmptyBracket(editor)) {
        return insertFragment(convertListNodesToPlainTextParagraphs(fragment));
      }

      if (
        hasListNodes(fragment) &&
        countListItemsInFragment(fragment) === 1 &&
        !isCursorInEmptyParagraph(editor) &&
        !isEntireParagraphLineSelected(editor)
      ) {
        return insertFragment(flattenTopLevelListNodesToParagraphNodes(fragment));
      }

      return insertFragment(fragment);
    };

    editor.normalizeNode = (entry: NodeEntry) => {
      const [node, path] = entry;

      if (downgradeOrphanListItemVariantAtNode(editor, node, path)) {
        return;
      }

      if (
        Element.isElement(node) &&
        node.type === options.pluginID &&
        Array.isArray(node.children)
      ) {
        // If the list node doesn't have a variant property, then set it to be an ordered list
        if (node.variant == null) {
          Transforms.setNodes(editor, { variant: LIST_VARIANTS.ol }, { at: path });
        }

        const listChildren: Array<Node> = node.children;
        if (listChildren.length === 0) {
          removeEmptyLists(editor);
          return;
        } else if (
          // Any of its children are invalid - either they aren't paragraph/list nodes or are
          // missing a variant property
          listChildren.some((child) => !isListChildValid(child))
        ) {
          for (const [child, childPath] of Array.from(
            Node.children(editor, path, { reverse: true })
          )) {
            const wrapOrRemoveChild = () => {
              if (Node.string(child).trim() === '') {
                Transforms.removeNodes(editor, { at: childPath });
              } else {
                Transforms.wrapNodes(
                  editor,
                  {
                    type: PARAGRAPH_PLUGIN_ID,
                    variant: LIST_VARIANTS.li,
                    children: [{ text: '' }],
                  },
                  { at: childPath }
                );
              }
            };

            if (!Element.isElement(child)) {
              wrapOrRemoveChild();
              continue;
            }

            if (child.type !== PARAGRAPH_PLUGIN_ID && child.type !== LIST_PLUGIN_ID) {
              wrapOrRemoveChild();
              continue;
            }

            if (child.type === LIST_PLUGIN_ID) {
              Transforms.setNodes(editor, { variant: LIST_VARIANTS.ol }, { at: childPath });
            }
          }
          return;
        }
      }

      normalizeNode(entry);
    };

    return withListShortcuts(editor);
  };
